import type { RxJsonSchema } from 'rxdb'

/**
 * CustomFieldDefinition Document
 *
 * Workspace-level custom field definitions. Each record defines one
 * user-created field that can be attached to tasks based on scoping rules.
 *
 * Scoping: workItemTypeIds and projectIds are denormalized from backend
 * junction tables. Empty arrays mean the field is hidden everywhere
 * (explicit opt-in). When both are non-empty, AND logic applies.
 *
 * Layout: customFieldTabId is required and points at a CustomFieldTab.
 * customFieldGroupId is optional (null = ungrouped within the tab).
 */
export interface CustomFieldDefinitionDocument {
  id: string
  envId: string
  name: string
  fieldType: 'string' | 'number' | 'date' | 'list'
  sortOrder: string | null
  isRequired: boolean

  /** Required FK to a CustomFieldTab in the same workspace. */
  customFieldTabId: string

  /** Optional FK to a CustomFieldGroup. null = ungrouped within the tab. */
  customFieldGroupId: string | null

  /** JSON-serialized type-specific constraints */
  config: string | null

  /** Denormalized scoping arrays (from backend junction tables) */
  workItemTypeIds: Array<string>
  projectIds: Array<string>

  /** JSON-serialized list options: [{ id, label, color, sortOrder }] */
  options: string | null

  isDeleted: boolean

  // Audit fields
  createdOn: number
  createdBy: string
  updatedOn: number
  updatedBy: string
  deletedOn?: number | null
  deletedBy?: string | null

  // RxDB replication metadata
  updatedAt?: number
}

export const customFieldDefinitionSchema: RxJsonSchema<CustomFieldDefinitionDocument> = {
  version: 1,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: {
      type: 'string',
      maxLength: 36,
    },
    envId: {
      type: 'string',
      maxLength: 36,
    },
    name: {
      type: 'string',
      maxLength: 100,
    },
    fieldType: {
      type: 'string',
      enum: ['string', 'number', 'date', 'list'],
      maxLength: 10,
    },
    sortOrder: {
      type: ['string', 'null'],
      maxLength: 50,
    },
    isRequired: {
      type: 'boolean',
      default: false,
    },
    customFieldTabId: {
      type: 'string',
      maxLength: 36,
    },
    customFieldGroupId: {
      type: ['string', 'null'],
      maxLength: 36,
    },
    config: {
      type: ['string', 'null'],
      maxLength: 2000,
    },
    workItemTypeIds: {
      type: 'array',
      items: {
        type: 'string',
        maxLength: 36,
      },
      default: [],
    },
    projectIds: {
      type: 'array',
      items: {
        type: 'string',
        maxLength: 36,
      },
      default: [],
    },
    options: {
      type: ['string', 'null'],
      maxLength: 10000,
    },
    isDeleted: {
      type: 'boolean',
      default: false,
    },

    // Audit fields
    createdOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    createdBy: {
      type: 'string',
      maxLength: 36,
    },
    updatedOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    updatedBy: {
      type: 'string',
      maxLength: 36,
    },
    deletedOn: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    deletedBy: {
      type: ['string', 'null'],
      maxLength: 36,
    },

    // RxDB replication metadata
    updatedAt: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
  },
  required: [
    'id',
    'envId',
    'name',
    'fieldType',
    'isRequired',
    'customFieldTabId',
    'isDeleted',
    'createdOn',
    'createdBy',
    'updatedOn',
    'updatedBy',
  ],
  indexes: [
    ['envId', 'isDeleted'],
    ['customFieldTabId', 'isDeleted'],
    'updatedOn',
  ],
}

export const customFieldDefinitionMigrationStrategies = {
  // v0 -> v1: strip removed `displayLocation`; backfill `customFieldGroupId`
  // (nullable) and `customFieldTabId` (required — sentinel empty string for
  // pre-feature docs; the customFieldTabs feature didn't exist when those
  // docs were created so they should be re-created post-migration).
  1: (oldDoc: any): any => {
    const { displayLocation, ...rest } = oldDoc
    return {
      ...rest,
      customFieldGroupId: oldDoc.customFieldGroupId ?? null,
      customFieldTabId: oldDoc.customFieldTabId ?? '',
    }
  },
}
